import { Box, Stack } from '@mui/material';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { FeedbackRatingButtons } from '@/components/form/baseInputs/Button/FeedbackRatingButtons';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { CopyOptionsMenuItems } from '@/modules/common/CopyOptionsMenuItems/CopyOptionsMenuItems';
import { COLORS } from '@/styles/tokens/colors';

type ChatFeedbackRating = 'good' | 'bad';

interface ChatFeedbackButtonsProps {
  rating: ChatFeedbackRating | null;
  ratingDisabled?: boolean;
  onRate: (rating: ChatFeedbackRating) => void;
  onCopy: () => void;
  /**
   * When provided (i.e. the message carries citations), the copy button opens
   * a menu offering "Copy text with citation reference" (this handler) and
   * "Copy text only" (`onCopy`). When omitted, the copy button invokes
   * `onCopy` directly.
   */
  onCopyWithCitation?: () => void;
}

function CopyButton({
  onCopy,
  onCopyWithCitation,
}: Pick<ChatFeedbackButtonsProps, 'onCopy' | 'onCopyWithCitation'>) {
  if (!onCopyWithCitation) {
    return (
      <IconButton
        icon={Copy07Icon}
        size="xs"
        variant="transparent"
        ariaLabel="Copy response"
        iconProps={{
          sx: {
            color: COLORS.TEXT.SECONDARY,
          },
        }}
        onClick={onCopy}
      />
    );
  }

  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      square
      hideChevron
      aria-label="Copy response"
      label={<Copy07Icon size={16} sx={{ color: COLORS.TEXT.SECONDARY }} />}
    >
      <CopyOptionsMenuItems
        onCopyWithCitation={onCopyWithCitation}
        onCopyTextOnly={onCopy}
      />
    </ButtonWithPopover>
  );
}

export function ChatFeedbackButtons({
  rating,
  ratingDisabled = false,
  onRate,
  onCopy,
  onCopyWithCitation,
}: ChatFeedbackButtonsProps) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ px: 1, pb: 0.5 }}>
      <FeedbackRatingButtons
        rating={rating}
        disabled={ratingDisabled}
        likeAriaLabel="I like this response"
        dislikeAriaLabel="I dislike this response"
        onRate={onRate}
      />
      <Box display="flex">
        <CopyButton onCopy={onCopy} onCopyWithCitation={onCopyWithCitation} />
      </Box>
    </Stack>
  );
}
